import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexProvider } from "convex/react";
import { HelmetProvider } from "react-helmet-async";
import { PostHogProvider } from "@posthog/react";
import posthog from "posthog-js";
import { toast } from "sonner";
import { configureNotifier } from "@heskala/core/notify";
import { convex, setConvexUrl } from "@/lib/convex";
import { installDevtools } from "@/lib/devtools/install";
import { routeTree } from "@/app/routeTree.gen";
import { I18nProvider } from "@/components/providers/I18nProvider";
import "@/app/index.css";
import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "@fontsource/schibsted-grotesk/500.css";
import "@/app/theme.css";
import { isLocalInstallEdition } from "@/lib/edition/edition";
import { applyNativeShellBehavior } from "@/lib/desktop/native-shell";
import { DesktopOAuthListener } from "@/components/auth/desktop-oauth-listener";
import { currentSiteHost } from "@/lib/sites/host";
import { isPublicSitePath } from "@/lib/sites/paths";
import { registerServiceWorker } from "@/lib/pwa/register-sw";

// Subdominio de un sitio público (`<slug>.<VITE_SITES_BASE_DOMAIN>`, o
// `?site=<slug>` en desarrollo): se monta solo el sitio (site-host-app.tsx),
// sin el router, el login, DevTools ni el service worker del ERP.
const siteHost = currentSiteHost();
// El mismo sitio dentro de la app (`/s/<slug>`): corre en el router del ERP,
// pero sin lo que es de la app (ver isPublicSitePath).
const onPublicSitePath = !siteHost && isPublicSitePath(window.location.pathname);

if (!siteHost) {
  // DevTools para desarrolladores y agentes de IA (dev o debugMode): ver AGENTS.md.
  // En `/s/<slug>` solo mide (errores, llamadas a Convex): el botón y el panel
  // están en el layout `_authenticated`, que el sitio no usa.
  installDevtools();

  // En el build de escritorio (release) desactiva el menú contextual y atajos de
  // navegador para que la app se sienta nativa. No-op en web y en dev.
  applyNativeShellBehavior();

  // Service worker de la PWA (app-shell offline). Ver lib/pwa/register-sw.ts.
  // En un sitio público no: el visitante no usa el ERP, y tampoco se le
  // ofrece instalar «Heskala ERP» (mismo criterio que en el subdominio).
  if (onPublicSitePath) document.querySelector('link[rel="manifest"]')?.remove();
  else registerServiceWorker();

  // Registra el notificador (NotifierPort) que usa la lógica de negocio portable
  // de @heskala/core (p. ej. el autosave de cotizaciones) → sonner en web.
  configureNotifier({
    success: (m) => toast.success(m),
    error: (m) => toast.error(m),
    info: (m) => toast.info(m),
  });
}

const options = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  ui_host: "https://us.posthog.com",
  defaults: "2026-01-30",
  capture_dead_clicks: true, //
} as const;

// Router de la app. Se crea solo fuera de los sitios públicos: en un host de
// sitio no hay rutas del ERP (ni sus redirecciones al login).
function createAppRouter() {
  const router = createRouter({
    routeTree,
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
  });

  router.subscribe("onLoad", () => {
    posthog.capture("$pageview", {
      $current_url: window.location.href,
    });
  });
  return router;
}

// Register router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

// Get root element
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

// Prevent multiple React root creations using a global flag
// This can happen during HMR in development
declare global {
  interface Window {
    __REACT_ROOT_CREATED__?: boolean;
  }
}

// Switch de deployment de Convex: cuando el plugin Vite lo dispara via
// server.ws.send("heskala:convex-set-url"), reescribimos .env.local y hacemos
// full-reload. Tambien soportamos cambio en runtime via window event para el
// caso en que se quiera evitar el reload (no se usa hoy pero queda listo).
if (typeof window !== "undefined") {
  // HMR del plugin: el cliente recibe el WebSocket "heskala:convex-set-url" y
  // ademas Vite dispara full-reload por el cambio en .env.local. Hacemos lo
  // propio para que el ConvexReactClient quede apuntando a la nueva URL
  // incluso antes del reload (defensa en profundidad).
  try {
    const hot = (import.meta as unknown as { hot?: { on: (ev: string, cb: (p: unknown) => void) => void } }).hot;
    hot?.on("heskala:convex-set-url", (p) => {
      const data = p as { url: string; siteUrl: string };
      if (data?.url && data?.siteUrl) setConvexUrl(data);
    });
  } catch {
    // sin HMR disponible (build de prod): noop
  }
}

if (!window.__REACT_ROOT_CREATED__) {
  // Create and render app
  const root = createRoot(rootElement);
  const isOnPrem = isLocalInstallEdition();

  if (siteHost) {
    // En su propio chunk: el bundle de la app no incluye el sitio.
    void import("@/components/sites/host/site-host-app").then(({ renderSiteHost }) =>
      renderSiteHost(root, siteHost.slug),
    );
  } else {
    const router = createAppRouter();
    isOnPrem
      ? root.render(
          <StrictMode>
            <I18nProvider>
              <HelmetProvider>
                <ConvexProvider client={convex}>
                  <ConvexAuthProvider client={convex}>
                    <RouterProvider router={router} />
                  </ConvexAuthProvider>
                </ConvexProvider>
              </HelmetProvider>
            </I18nProvider>
          </StrictMode>,
        )
      : root.render(
          <StrictMode>
            <I18nProvider>
              <HelmetProvider>
                <ConvexProvider client={convex}>
                  <ConvexAuthProvider client={convex}>
                    <DesktopOAuthListener />
                    <RouterProvider router={router} />
                  </ConvexAuthProvider>
                </ConvexProvider>
              </HelmetProvider>
            </I18nProvider>
          </StrictMode>,
        );
  }

  // Mark that we've created the root
  window.__REACT_ROOT_CREATED__ = true;
} else {
  console.warn("React root already created. Skipping createRoot() call");
}
